import { useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { Lock, Eye, EyeOff, Loader2, CheckCircle2, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) { setError("Password must be at least 8 characters."); return; }
    if (password !== confirm) { setError("Passwords don't match."); return; }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      setTimeout(() => navigate("/login", { replace: true }), 1800);
    } catch (e) {
      const d = e?.response?.data?.detail;
      setError(typeof d === "string" ? d : "Couldn't reset password. Try requesting a new link.");
    } finally {
      setBusy(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-[60vh] grid place-items-center px-4" data-testid="reset-password-page">
        <div className="text-center max-w-md">
          <h1 className="font-heading text-xl font-semibold text-slate-900 dark:text-slate-100 mb-2">Missing reset link</h1>
          <p className="text-slate-600 dark:text-slate-300 text-sm">This page requires a token from your email.</p>
          <Link to="/forgot-password" className="inline-block mt-4 text-sm text-sky-700 dark:text-sky-300 hover:underline">Request a new link →</Link>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="min-h-[60vh] grid place-items-center px-4" data-testid="reset-password-page">
        <div className="text-center max-w-md">
          <CheckCircle2 className="w-12 h-12 mx-auto mb-4 text-emerald-600" data-testid="reset-password-ok"/>
          <h1 className="font-heading text-xl font-semibold text-slate-900 dark:text-slate-100">Password reset</h1>
          <p className="text-slate-600 dark:text-slate-300 mt-2">Redirecting you to sign in…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[60vh] grid place-items-center px-4 py-12" data-testid="reset-password-page">
      <div className="w-full max-w-md">
        <div className="mb-6 text-center">
          <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-sky-100 dark:bg-sky-950/40 grid place-items-center">
            <Lock className="w-5 h-5 text-sky-700 dark:text-sky-300"/>
          </div>
          <h1 className="font-heading text-2xl font-semibold text-slate-900 dark:text-slate-100">Choose a new password</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Make it at least 8 characters.</p>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">New password</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 dark:text-slate-500"/>
              <input
                data-testid="reset-password-input"
                type={showPw ? "text" : "password"}
                required
                minLength={8}
                value={password}
                onChange={e => setPassword(e.target.value)}
                autoComplete="new-password"
                className="w-full pl-10 pr-10 py-2.5 border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-sky-600 focus:border-sky-600"
              />
              <button type="button" onClick={() => setShowPw(s => !s)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 dark:text-slate-500 hover:text-slate-700"
                      data-testid="reset-password-toggle"
                      aria-label={showPw ? "Hide password" : "Show password"}>
                {showPw ? <EyeOff className="w-4 h-4"/> : <Eye className="w-4 h-4"/>}
              </button>
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">Confirm password</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 dark:text-slate-500"/>
              <input
                data-testid="reset-password-confirm"
                type={showPw ? "text" : "password"}
                required
                minLength={8}
                value={confirm}
                onChange={e => setConfirm(e.target.value)}
                autoComplete="new-password"
                className="w-full pl-10 pr-3 py-2.5 border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-sky-600 focus:border-sky-600"
              />
            </div>
          </div>
          {error && (
            <div data-testid="reset-password-error" className="text-sm text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 rounded-md px-3 py-2">
              {error}
            </div>
          )}
          <button
            data-testid="reset-password-submit"
            type="submit"
            disabled={busy}
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md bg-slate-900 hover:bg-slate-700 text-white text-sm font-medium disabled:opacity-60"
          >
            {busy && <Loader2 className="w-4 h-4 animate-spin"/>}
            Reset password
            {!busy && <ArrowRight className="w-4 h-4"/>}
          </button>
        </form>
      </div>
    </div>
  );
}
